import { ThemeOptions, createTheme, Shadows } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { Orelega_One, Poppins, Caveat, Raleway } from "next/font/google";
import React from "react";

// Add custom handwritten variant to MUI theme typings

declare module "@mui/material/styles" {
  interface TypographyVariants {
    handwritten: React.CSSProperties;
    poppins: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    handwritten?: React.CSSProperties;
    poppins?: React.CSSProperties;
  }

  // Extend the palette to include our custom colors
  interface Palette {
    color: {
      main: {
        primary: string;
        secondary: string;
      };
      content: {
        textDark: string;
        border: string;
        hover: string;
        background: string;
        shadow: string;
        shadowLight: string;
        shadowDark: string;
        shadowInvert: string;
        shadowInvertLight: string;
        shadowInvertDark: string;
        shadowBlue: string;
        shadowBlueLight: string;
        shadowBlueDark: string;
      };
      cyan: {
        light: string;
        medium: string;
        dark: string;
      };
      blue: {
        light: string;
        medium: string;
        dark: string;
      };
      orange: {
        medium: string;
        dark: string;
      };
      yellow: {
        light: string;
        medium: string;
        dark: string;
      };
      red: {
        light: string;
        medium: string;
        dark: string;
      };
      green: {
        light: string;
        medium: string;
        dark: string;
      };
    };
  }

  interface PaletteOptions {
    color?: {
      main?: {
        primary?: string;
        secondary?: string;
      };
      content?: {
        textDark?: string;
        border?: string;
        hover?: string;
        background?: string;
        shadow?: string;
        shadowLight?: string;
        shadowDark?: string;
        shadowInvert?: string;
        shadowInvertLight?: string;
        shadowInvertDark?: string;
        shadowBlue?: string;
        shadowBlueLight?: string;
        shadowBlueDark?: string;
      };
      cyan?: {
        light?: string;
        medium?: string;
        dark?: string;
      };
      blue?: {
        light?: string;
        medium?: string;
        dark?: string;
      };
      orange?: {
        medium?: string;
        dark?: string;
      };
      yellow?: {
        light?: string;
        medium?: string;
        dark?: string;
      };
      red?: {
        light?: string;
        medium?: string;
        dark?: string;
      };
      green?: {
        light?: string;
        medium?: string;
        dark?: string;
      };
    };
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    handwritten: true;
    poppins: true;
  }
}

const orelegaOne = Orelega_One({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
});

const raleway = Raleway({
  weight: ["400", "500", "700"],
  subsets: ["latin"],
  display: "swap",
});

const poppins = Poppins({
  weight: ["400", "500", "700", "900"],
  subsets: ["latin"],
  display: "swap",
});

const caveat = Caveat({
  weight: ["400", "500", "600", "700"],
  subsets: ["latin"],
  display: "swap",
});

const themeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 978,
      lg: 1140,
      xl: 1540,
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#4048ff",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#243050",
      contrastText: "#ffffff",
    },
    error: {
      main: "#ff1744",
    },
    info: {
      main: "#f3f7fa",
    },
    success: {
      main: "#2e7d32",
    },
    // Custom color palette migrated from CSS variables (updated to match commit 595f6ac)
    color: {
      main: {
        primary: "#4048ff",
        secondary: "#243050", // Updated from #FFD2D3 to match commit
      },
      content: {
        textDark: "#939EB8",
        border: "#C1EAF4", // Updated from #f2f2f2
        hover: "#000BFF", // Updated from #6470ef
        background: "#EFF6F9", // Updated from #4f5464 (this is bg-content)
        shadow: "rgba(0,0,0,0.25)",
        shadowLight: "rgba(0,0,0,0.01)",
        shadowDark: "rgba(0,0,0,0.65)",
        shadowInvert: "rgba(255,255,255,0.25)",
        shadowInvertLight: "rgba(255,255,255,0.1)",
        shadowInvertDark: "rgba(255,255,255,0.65)",
        shadowBlue: "rgba(64,72,255,0.25)",
        shadowBlueLight: "rgba(64,72,255,0.1)",
        shadowBlueDark: "rgba(64,72,255,0.65)",
      },
      cyan: {
        light: "#EAF3F7",
        medium: "#C1EAF4",
        dark: "#6BD9E7",
      },
      blue: {
        light: "#3646EB",
        medium: "#2C3D83",
        dark: "#243050",
      },
      orange: {
        medium: "#E17A7E",
        dark: "#EC6A5F",
      },
      yellow: {
        light: "#FFF5D3",
        medium: "#FFE4BB",
        dark: "#F2C785",
      },
      red: {
        light: "#FFE4E0",
        medium: "#ffb6c1",
        dark: "#DC3911",
      },
      green: {
        light: "#E1FFE0",
        medium: "#C5F1BF",
        dark: "#61C554",
      },
    },
  },
  shape: {
    borderRadius: 14,
  },
  shadows: ["none", ...Array(25).fill("none")] as Shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          backgroundColor: "transparent",
          textDecoration: "none",
          fontWeight: 400,
          color: "#3646EB", // Using your blue.light color
          WebkitTransition: "0.25s",
          transition: "0.25s",
          "&:hover": {
            color: "#243050", // Using your blue.dark color for hover
            textDecoration: "none",
          },
          "&:not([href]):not([tabindex])": {
            color: "inherit",
            textDecoration: "none",
          },
          "&:not([href]):not([tabindex]):hover, &:not([href]):not([tabindex]):focus":
            {
              color: "inherit",
              textDecoration: "none",
            },
          "&:not([href]):not([tabindex]):focus": {
            outline: 0,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 18,
        },
        sizeSmall: {
          fontSize: "0.9rem",
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "::placeholder": {
            color: "red",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: "none",
          display: "inline-block",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "0.9rem",
          borderRadius: "2rem",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: "0.75rem" },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "1px solid #bbdefb",
        },
        columnHeaders: {
          backgroundColor: "#e3f2fd",
          border: "none",
        },
        footerContainer: {
          borderBottomLeftRadius: "0.75rem",
          borderBottomRightRadius: "0.75rem",
          backgroundColor: "#e3f2fd",
        },
        columnHeaderTitle: {
          fontWeight: "900",
          color: "#555",
        },
        sortIcon: {
          color: "#e17a7e",
        },
        menuIconButton: {
          color: "#e17a7e",
        },
        panel: {
          "& .MuiPaper-rounded": {
            borderRadius: "1rem",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.1)",
          },
        },
        menu: {
          "& .MuiPaper-rounded": {
            borderRadius: "1rem",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
  },
  typography: {
    fontFamily: raleway.style.fontFamily,
    fontSize: 14,
    h1: {
      fontFamily: orelegaOne.style.fontFamily,
      fontSize: "2.5rem",
      fontWeight: 900,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: orelegaOne.style.fontFamily,
      fontSize: "2rem",
      fontWeight: 900,
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: orelegaOne.style.fontFamily,
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: orelegaOne.style.fontFamily,
      fontSize: "1.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: orelegaOne.style.fontFamily,
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h6: {
      fontFamily: orelegaOne.style.fontFamily,
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: 1.2,
    },
    body1: {
      fontFamily: raleway.style.fontFamily,
      fontSize: 14,
      fontWeight: 400,
    },
    body2: {
      fontFamily: poppins.style.fontFamily,
      fontSize: 12,
      fontWeight: 400,
    },
    button: {
      fontFamily: poppins.style.fontFamily,
      fontWeight: 500,
      fontSize: "0.5rem",
    },
    caption: {
      fontFamily: poppins.style.fontFamily,
      fontSize: "0.8rem",
    },
    handwritten: {
      fontFamily: caveat.style.fontFamily,
      fontSize: "3.5rem",
      color: "#4048ff", // Updated to use direct color instead of CSS variable
      fontWeight: 400,
      letterSpacing: "-0.1rem",
      lineHeight: "3rem",
    },
    subtitle1: {
      fontSize: "1.05rem",
      color: "#555",
    },
    subtitle2: {
      fontSize: "1rem",
      fontWeight: "bold",
      color: "#777",
    },
  },
};

export const theme = createTheme(themeOptions);
